import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, Rocket, FileText, ArrowRight, Eye, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface Brief {
  id: string;
  project_name: string;
  industry: string;
  review_status: string;
  created_at: string;
  quoted_amount: number | null;
  invoice_url: string | null;
  invoice_storage_path: string | null;
}

const statusMap: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  pending: { label: 'Under Review', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: <Clock className="w-3.5 h-3.5" /> },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  paid: { label: 'In Production', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: <Rocket className="w-3.5 h-3.5" /> },
  declined: { label: 'Needs Changes', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: <XCircle className="w-3.5 h-3.5" /> },
};

export const MyProposalsSection = () => {
  const { user } = useAuth();
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      const { data } = await supabase
        .from('launch_kit_briefs')
        .select('id, project_name, industry, review_status, created_at, quoted_amount, invoice_url, invoice_storage_path')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setBriefs((data || []) as Brief[]);
      setLoading(false);
    })();
  }, [user]);

  if (!user || loading || briefs.length === 0) return null;

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-card/40 border-y border-border/30">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between mb-8 flex-wrap gap-4"
        >
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold mb-3">
              <FileText className="w-3.5 h-3.5" /> Your Proposals
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">My Project Proposals</h2>
            <p className="text-muted-foreground mt-1">Track the status of every brief you've sent us.</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/launch-kit/status">
              View all <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </motion.div>

        <div className="space-y-3">
          {briefs.slice(0, 4).map((b, i) => {
            const s = statusMap[b.review_status] || statusMap.pending;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-5 rounded-2xl bg-card border border-border/40 flex items-center justify-between gap-4 flex-wrap hover:border-accent/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <h3 className="font-semibold text-foreground truncate">{b.project_name}</h3>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>
                      {s.icon}{s.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {b.industry} · Submitted {new Date(b.created_at).toLocaleDateString()}
                    {b.quoted_amount ? ` · Quote $${Number(b.quoted_amount).toLocaleString()}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {b.review_status === 'approved' && (b.invoice_url || b.invoice_storage_path) && (
                    <Button asChild size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                      <Link to="/launch-kit/status">
                        Pay Invoice <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                      </Link>
                    </Button>
                  )}
                  <Button asChild size="sm" variant="outline">
                    <Link to="/launch-kit/status">
                      <Eye className="w-4 h-4 mr-1.5" /> View
                    </Link>
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MyProposalsSection;
